/* Agent identity for Agent Desk.
 *
 * Each row in the `agents` table is one human. The SignInModal lists
 * those rows and the agent picks themselves; we persist the row's id
 * (`agentId`) and name in localStorage. The browser_id pattern that
 * preceded this minted a fresh UUID per tab, which let one human's
 * hard refresh register two simultaneous Verto sessions against a
 * shared SIP credential and split where Telnyx delivered INVITEs.
 *
 * `agentId` is the canonical identity; `name` is cached for UI labels
 * but the server treats it as advisory (the row id is authoritative). */

import { useEffect, useState } from "react";

export type AgentIdentity = {
  agentId: number;
  name: string;
  signedInAt: string;
};

const STORAGE_KEY = "agent-desk:identity";

function safeRead(): AgentIdentity | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AgentIdentity>;
    if (
      typeof parsed.agentId !== "number" ||
      !Number.isInteger(parsed.agentId) ||
      parsed.agentId <= 0 ||
      typeof parsed.name !== "string" ||
      !parsed.name.trim()
    ) {
      return null;
    }
    return {
      agentId: parsed.agentId,
      name: parsed.name,
      signedInAt:
        typeof parsed.signedInAt === "string"
          ? parsed.signedInAt
          : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

function safeWrite(value: AgentIdentity | null): void {
  try {
    if (value === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* private mode — best-effort */
  }
}

/**
 * Subscribe to the current agent identity. Returns null until the user
 * has picked themselves from the agents dropdown. `signIn` writes a
 * new identity for the chosen agent_id; `clear` wipes it.
 */
export function useAgentIdentity(): {
  identity: AgentIdentity | null;
  signIn: (agentId: number, name: string) => void;
  clear: () => void;
} {
  const [identity, setIdentity] = useState<AgentIdentity | null>(safeRead);

  // Cross-tab sync — a sign-in (or sign-out) in one tab updates others
  // so the rail's "Signed in as ..." label stays consistent.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return;
      setIdentity(safeRead());
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  function signIn(agentId: number, name: string) {
    const trimmed = name.trim();
    if (!trimmed || !Number.isInteger(agentId) || agentId <= 0) return;
    const existing = safeRead();
    const next: AgentIdentity = {
      agentId,
      name: trimmed,
      signedInAt:
        existing?.agentId === agentId
          ? (existing.signedInAt ?? new Date().toISOString())
          : new Date().toISOString(),
    };
    safeWrite(next);
    setIdentity(next);
  }

  function clear() {
    safeWrite(null);
    setIdentity(null);
  }

  return { identity, signIn, clear };
}

/**
 * Headers to attach to mutating requests so the backend has audit
 * context. Returns an empty object when no identity is set — callers
 * spread it into their fetch headers unconditionally.
 */
export function agentHeaders(
  identity: AgentIdentity | null,
): Record<string, string> {
  if (!identity) return {};
  return {
    "X-Agent-Id": String(identity.agentId),
    "X-Agent-Name": identity.name,
  };
}
